import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import Api, { exceptionNotificationAPI } from '../../services/api';
import { Container } from './styles';
import ListTreeItemsEditor from './ListTreeItemsEditor';
import ListTreeImportModal from './ListTreeImportModal';
import { Form } from '@unform/web';
import FormInput from '../../components/FormInput';
import Button from '../../components/Button';
import CancelButton from '../../components/CancelButton';
import { success_message } from '../../components/Toast';
import getValidationErrors from '../../utils/getValidationErrors';

const EXAMPLE_JSON = `[
  {
    "nodeID": "1",
    "value": "Seedbed",
    "parentNodeID": null,
    "items": [
      {
        "nodeID": "2",
        "value": "Seed",
        "parentNodeID": "1",
        "items": [
          { "nodeID": "3", "value": "VA Seed - envelope", "parentNodeID": "2", "unitMeasure": "kg" },
          { "nodeID": "4", "value": "VA Seed - pot", "parentNodeID": "2", "unitMeasure": "g" }
        ]
      }
    ]
  }
]`;

const RESERVED_KEYS = new Set(['nodeID', 'NodeID', 'value', 'Value', 'parentNodeID', 'ParentNodeID', 'items', 'Items', 'code', 'Code']);

/**
 * Extracts extra properties from a node (e.g. unitMeasure) to be saved as ListTreeItemAttr.
 * Reserved keys (nodeID, value, parentNodeID, items, code) are excluded.
 */
function getAttrsFromNode(node) {
    const attrs = {};
    if (!node || typeof node !== 'object') return attrs;
    for (const key of Object.keys(node)) {
        if (RESERVED_KEYS.has(key)) continue;
        const v = node[key];
        if (v === null || v === undefined) continue;
        attrs[key] = typeof v === 'object' ? JSON.stringify(v) : String(v);
    }
    return attrs;
}

/**
 * Converts a node from the JSON (flat or nested) to a canonical flat item.
 * Includes attrs: extra columns (e.g. unitMeasure) to be stored in ListTreeItemAttr.
 */
function nodeToItem(node, parentNodeID) {
    const nodeID = String(node.nodeID ?? node.NodeID ?? '');
    const value = String(node.value ?? node.Value ?? '');
    const parentNodeIDFromNode = node.parentNodeID != null ? String(node.parentNodeID) : (node.ParentNodeID != null ? String(node.ParentNodeID) : null);
    const attrs = getAttrsFromNode(node);
    return {
        nodeID,
        value,
        parentNodeID: parentNodeIDFromNode ?? parentNodeID ?? null,
        code: node.code ?? node.Code ?? null,
        attrs
    };
}

/**
 * Flattens JSON that can be either:
 * - Flat array: [ { nodeID, value, parentNodeID }, ... ]
 * - Nested array: [ { nodeID, value, parentNodeID, items: [ ... ] }, ... ]
 * Any extra properties (e.g. unitMeasure) are collected into attrs and saved as ListTreeItemAttr.
 * Returns a flat array of { nodeID, value, parentNodeID, code, attrs }.
 */
function flattenTreeToItems(nodes, parentNodeID = null) {
    if (!Array.isArray(nodes)) return [];
    const result = [];
    for (const node of nodes) {
        const item = nodeToItem(node, parentNodeID);
        result.push(item);
        const children = node.items ?? node.Items;
        if (Array.isArray(children) && children.length > 0) {
            const childItems = flattenTreeToItems(children, item.nodeID);
            result.push(...childItems);
        }
    }
    return result;
}

const ListTreeForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const formRef = useRef(null);
    const [data, setData] = useState(null);
    const [items, setItems] = useState([]);
    const [jsonInput, setJsonInput] = useState('');
    const [importing, setImporting] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [loadingItems, setLoadingItems] = useState(false);
    const isEdit = id != null && id !== 'new';

    const activeOptions = [
        { value: true, text: 'Yes' },
        { value: false, text: 'No' }
    ];

    const fields = [
        { label: 'Name', name: 'name', type: 'input', required: true },
        { label: 'Type', name: 'type', type: 'number', required: true },
        { label: 'External ID', name: 'externalID', type: 'number', required: false },
        { label: 'Culture ID', name: 'cultureID', type: 'number', required: false },
        { label: 'Is Active', name: 'isActive', type: 'select', options: activeOptions }
    ];

    useEffect(() => {
        if (isEdit) {
            loadListTree();
            loadItems();
        }
    }, [id]);

    const loadListTree = async () => {
        try {
            const result = await Api.get(`ListTree/get?id=${id}`);
            if (result?.data?.response) {
                formRef.current?.setData(result.data.response);
                setData(result.data.response);
            }
        } catch (error) {
            exceptionNotificationAPI(error);
        }
    };

    const loadItems = async () => {
        if (!id) return;
        setLoadingItems(true);
        try {
            const result = await Api.get(`ListTree/getItems?listTreeID=${id}`);
            if (result?.data?.response && Array.isArray(result.data.response)) {
                setItems(result.data.response);
            } else {
                setItems([]);
            }
        } catch (error) {
            exceptionNotificationAPI(error);
            setItems([]);
        } finally {
            setLoadingItems(false);
        }
    };

    const handleSubmit = async (formData) => {
        try {
            const schema = Yup.object().shape({
                name: Yup.string().required('Name is required'),
                type: Yup.number().required('Type is required')
            });
            await schema.validate(formData, { abortEarly: false });

            const payload = {
                listTreeID: isEdit ? parseInt(id, 10) : 0,
                name: formData.name,
                type: Number(formData.type) || 0,
                externalID: formData.externalID ? Number(formData.externalID) : null,
                cultureID: formData.cultureID ? Number(formData.cultureID) : null,
                isActive: formData.isActive === true || formData.isActive === 'true'
            };

            const url = isEdit ? 'ListTree/edit' : 'ListTree/new';
            await Api.post(url, payload);
            success_message('Saved successfully');
            if (!isEdit) {
                const listResult = await Api.get('ListTree/getAll');
                const list = listResult?.data?.response ?? [];
                const matches = list.filter((x) => x.name === payload.name);
                const created = matches.length ? matches.reduce((a, b) => (a.listTreeID > b.listTreeID ? a : b)) : null;
                if (created) navigate(`/listTree/edit/${created.listTreeID}`);
                else navigate('/listTree');
            } else {
                loadListTree();
            }
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                const errors = getValidationErrors(error);
                formRef.current?.setErrors(errors);
                return;
            }
            exceptionNotificationAPI(error);
        }
    };

    const handleImportJson = async () => {
        if (!id) {
            success_message('Save the list first, then import items.');
            return;
        }
        let parsed;
        try {
            parsed = JSON.parse(jsonInput);
        } catch (e) {
            exceptionNotificationAPI({ response: { data: { message: 'Invalid JSON. Use a flat array or nested array (objects can have an "items" array for children).' } } });
            return;
        }
        if (!Array.isArray(parsed)) {
            exceptionNotificationAPI({ response: { data: { message: 'JSON must be an array of items (flat or nested with "items").' } } });
            return;
        }
        const itemsToImport = flattenTreeToItems(parsed);
        if (itemsToImport.length === 0) {
            exceptionNotificationAPI({ response: { data: { message: 'No valid items found. Each object must have nodeID and value.' } } });
            return;
        }
        setImporting(true);
        try {
            await Api.post('ListTree/importItems', {
                listTreeID: parseInt(id, 10),
                items: itemsToImport
            });
            success_message('Items imported successfully');
            setJsonInput('');
            loadItems();
            setShowImportModal(false);
        } catch (error) {
            exceptionNotificationAPI(error);
        } finally {
            setImporting(false);
        }
    };

    return (
        <Container className="container">
            <div>
                <h3>{isEdit ? 'Edit' : 'New'} List Tree</h3>
                <Form ref={formRef} onSubmit={handleSubmit}>
                    <table className="table table-striped">
                        <tbody>
                            {fields.map((f, i) => (
                                <tr key={i}>
                                    <td width="25%">
                                        <label>
                                            {f.label}
                                            {f.required !== false && <span className="required">*</span>}
                                        </label>
                                    </td>
                                    <td width="75%">
                                        <FormInput
                                            name={f.name}
                                            id={f.name}
                                            inputType={f.type}
                                            options={f.options}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="d-flex flex-row mt-2">
                        <CancelButton />
                        <Button type="submit">Save List</Button>
                    </div>
                </Form>

                {isEdit && (
                    <div className="mt-5">
                        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-2">
                            <div>
                                <h5 className="mb-2">List items</h5>
                                <p className="text-muted small mb-0">
                                    Edit the tree inline, or import via JSON / Excel to replace all items at once.
                                </p>
                            </div>
                            <Button type="button" onClick={() => setShowImportModal(true)}>
                                Import items
                            </Button>
                        </div>
                        <ListTreeItemsEditor
                            listTreeID={id}
                            listTreeName={data?.name ?? data?.Name ?? ''}
                            items={items}
                            loading={loadingItems}
                            onReload={loadItems}
                        />

                        <ListTreeImportModal
                            show={showImportModal}
                            onHide={() => setShowImportModal(false)}
                            listTreeID={parseInt(id, 10)}
                            exampleJson={EXAMPLE_JSON}
                            jsonInput={jsonInput}
                            setJsonInput={setJsonInput}
                            onImportJson={handleImportJson}
                            importingJson={importing}
                            onAfterImportSuccess={loadItems}
                        />
                    </div>
                )}
            </div>
        </Container>
    );
};

export default ListTreeForm;
